/**
 * VC-G4 — Dark mode 지원.
 *
 * ColorMode: 'light' | 'dark' | 'system'
 * - 'light' / 'dark': 명시적 사용자 선택. localStorage 'infergraph.colorMode' 에 저장.
 * - 'system': prefers-color-scheme 미디어쿼리 추적. 사용자 변경 시 reactive.
 *
 * effectiveMode 는 'light' | 'dark' (실제 적용 mode).
 */
import * as React from 'react'

export type ColorModePreference = 'light' | 'dark' | 'system'
export type EffectiveColorMode = 'light' | 'dark'

type ThemeModeContextValue = {
  preference: ColorModePreference
  effectiveMode: EffectiveColorMode
  setPreference: (next: ColorModePreference) => void
  toggle: () => void
}

const STORAGE_KEY = 'infergraph.colorMode'

const readStoredPreference = (): ColorModePreference => {
  if (typeof window === 'undefined') return 'system'
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (raw === 'light' || raw === 'dark' || raw === 'system') return raw
  } catch {
    // ignore (private window / disabled storage)
  }
  return 'system'
}

const systemPrefersDark = (): boolean => {
  if (typeof window === 'undefined') return false
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false
}

const resolveEffectiveMode = (pref: ColorModePreference): EffectiveColorMode => {
  if (pref === 'light' || pref === 'dark') return pref
  return systemPrefersDark() ? 'dark' : 'light'
}

const ThemeModeContext = React.createContext<ThemeModeContextValue | null>(null)

export const ThemeModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [preference, setPreferenceState] = React.useState<ColorModePreference>(() => readStoredPreference())
  const [effectiveMode, setEffectiveMode] = React.useState<EffectiveColorMode>(() =>
    resolveEffectiveMode(readStoredPreference())
  )

  // preference 변경 시 effectiveMode 재계산 + persist
  React.useEffect(() => {
    setEffectiveMode(resolveEffectiveMode(preference))
    try {
      window.localStorage.setItem(STORAGE_KEY, preference)
    } catch {
      // ignore
    }
  }, [preference])

  // system preference 변경 추적 (preference='system'일 때만 reactive)
  React.useEffect(() => {
    if (preference !== 'system') return
    if (typeof window === 'undefined' || !window.matchMedia) return
    const mql = window.matchMedia('(prefers-color-scheme: dark)')
    const onChange = () => {
      setEffectiveMode(mql.matches ? 'dark' : 'light')
    }
    mql.addEventListener?.('change', onChange) ?? mql.addListener?.(onChange)
    return () => {
      mql.removeEventListener?.('change', onChange) ?? mql.removeListener?.(onChange)
    }
  }, [preference])

  const setPreference = React.useCallback((next: ColorModePreference) => {
    setPreferenceState(next)
  }, [])

  const toggle = React.useCallback(() => {
    setPreferenceState((prev) => (resolveEffectiveMode(prev) === 'dark' ? 'light' : 'dark'))
  }, [])

  const value = React.useMemo(
    () => ({ preference, effectiveMode, setPreference, toggle }),
    [preference, effectiveMode, setPreference, toggle]
  )

  return <ThemeModeContext.Provider value={value}>{children}</ThemeModeContext.Provider>
}

export const useThemeMode = (): ThemeModeContextValue => {
  const ctx = React.useContext(ThemeModeContext)
  if (!ctx) {
    throw new Error('useThemeMode must be used within ThemeModeProvider')
  }
  return ctx
}

// optional / safe accessor — provider 외부에서도 호출 가능
export const useThemeModeSafe = (): ThemeModeContextValue => {
  const ctx = React.useContext(ThemeModeContext)
  return (
    ctx ?? {
      preference: 'light',
      effectiveMode: 'light',
      setPreference: () => undefined,
      toggle: () => undefined,
    }
  )
}
